import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ArrowBack,
  Cake,
  Celebration,
  Email,
  Forward,
  Phone,
  ReplyAll,
  Send,
  Sms,
  Visibility,
} from '@mui/icons-material';
import {
  Task,
  TaskChannel,
  TaskCompletionEmail,
  TaskCompletionText,
  TaskEvent,
} from '@otw/models/tasks';
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { useContacts } from 'hooks/contacts/useContacts';
import { getContactForTask } from '../../util/contactTaskActivity';
import {
  birthdayEmail,
  followUpEmail,
  leadLostEmail,
  mailTemplates,
  truckAnniversaryEmail,
} from '../../util/mail';
import {
  birthdayText,
  followUpText,
  leadLostText,
  smsTemplates,
  truckAnniversaryText,
} from '../../util/sms';

type EmailRenderer = (params: {
  customerName: string;
  personalMessage?: string;
  senderName?: string;
}) => Promise<{ subject: string; html: string }>;

type TextRenderer = (params: {
  customerName: string;
  personalMessage?: string;
  senderName?: string;
}) => Promise<{ body: string }>;

type SupportedTaskEvent = Extract<TaskEvent, 'birthday' | 'truck_anniversary' | 'follow_up' | 'lead_lost'>;

type Channel = TaskChannel;

interface EventConfig {
  emailTemplate: keyof typeof mailTemplates;
  textTemplate: keyof typeof smsTemplates;
  renderEmail: EmailRenderer;
  renderText: TextRenderer;
  title: string;
  subtitlePrefix: string;
  icon: React.ReactNode;
  accent: 'secondary' | 'success' | 'primary' | 'error';
  messageLabel: string;
  messagePlaceholder: string;
  messageHelper: string;
  composeIntro: string;
}

const EVENT_CONFIG: Record<SupportedTaskEvent, EventConfig> = {
  birthday: {
    emailTemplate: 'birthdayEmail',
    textTemplate: 'birthdayText',
    renderEmail: birthdayEmail,
    renderText: birthdayText,
    title: 'Send a birthday note',
    subtitlePrefix: 'Completing task',
    icon: <Cake />,
    accent: 'secondary',
    messageLabel: 'Personal birthday message',
    messagePlaceholder:
      "Add a personal touch — anything you'd like to add to the birthday note. Used in both email and text.",
    messageHelper:
      'Folded into the highlighted block of the email and added as a paragraph in the text. Leave blank to send only the standard greeting.',
    composeIntro: 'Send a warm birthday note to your customer.',
  },
  truck_anniversary: {
    emailTemplate: 'truckAnniversaryEmail',
    textTemplate: 'truckAnniversaryText',
    renderEmail: truckAnniversaryEmail,
    renderText: truckAnniversaryText,
    title: 'Send a truck-anniversary note',
    subtitlePrefix: 'Completing task',
    icon: <Celebration />,
    accent: 'success',
    messageLabel: 'Anniversary message',
    messagePlaceholder:
      "Optional: mention the truck, how long it's been, or anything else worth celebrating.",
    messageHelper:
      'Used by both the email and text templates. Leave blank for the default copy.',
    composeIntro: 'Celebrate another year on the road with your customer.',
  },
  follow_up: {
    emailTemplate: 'followUpEmail',
    textTemplate: 'followUpText',
    renderEmail: followUpEmail,
    renderText: followUpText,
    title: 'Send a follow-up',
    subtitlePrefix: 'Completing task',
    icon: <ReplyAll />,
    accent: 'primary',
    messageLabel: 'Follow-up message',
    messagePlaceholder:
      'What are you following up about? Pricing, a specific truck, paperwork, etc.',
    messageHelper:
      'Most follow-ups feel best with a real, specific message. Used by both email and text.',
    composeIntro: 'Quick, personalized follow-up wrapped in your standard templates.',
  },
  lead_lost: {
    emailTemplate: 'leadLostEmail',
    textTemplate: 'leadLostText',
    renderEmail: leadLostEmail,
    renderText: leadLostText,
    title: 'Send a re-engagement note',
    subtitlePrefix: 'Completing task',
    icon: <Forward />,
    accent: 'error',
    messageLabel: 'Re-engagement message',
    messagePlaceholder:
      'Optional: mention what was missing last time, share something new, or just keep it short and human.',
    messageHelper:
      'Used by both templates. Leave blank for the default open-door copy.',
    composeIntro: 'A no-pressure note that keeps the door open after a lost lead.',
  },
};

export const isSupportedCommunicationTaskEvent = (
  event: TaskEvent,
): event is SupportedTaskEvent => event in EVENT_CONFIG;

/**
 * Result returned by the parent's `sendCommunication` handler. The dialog
 * forwards these artifacts back through `onSent` so they can be persisted on
 * the task.
 */
export interface SentCommunicationResult {
  email?: TaskCompletionEmail | null;
  text?: TaskCompletionText | null;
}

interface CompleteTaskCommunicationDialogProps {
  open: boolean;
  task: Task | null;
  onClose: () => void;
  /**
   * Called once the selected channels have been queued. The parent should mark
   * the task complete and persist the returned artifacts.
   */
  onSent: (task: Task, result: SentCommunicationResult) => Promise<void> | void;
  /**
   * Renders + queues the email. Returns true on success.
   */
  sendEmail: (params: {
    to: string;
    customerName: string;
    personalMessage: string;
    senderName: string | null;
    templateName: keyof typeof mailTemplates;
  }) => Promise<boolean>;
  /**
   * Renders + queues the SMS. Returns the rendered body + Firestore doc id.
   */
  sendText: (params: {
    to: string;
    customerName: string;
    personalMessage: string;
    senderName: string | null;
    templateName: keyof typeof smsTemplates;
  }) => Promise<{ success: boolean; messageDocId?: string; body?: string; normalizedPhone?: string }>;
}

type Stage = 'compose' | 'preview';

const CompleteTaskCommunicationDialog: React.FC<CompleteTaskCommunicationDialogProps> = ({
  open,
  task,
  onClose,
  onSent,
  sendEmail,
  sendText,
}) => {
  const theme = useTheme();
  const { userInfo } = useAuth();
  const { contacts } = useContacts();
  const contact = useMemo(
    () => (task ? getContactForTask(contacts, task) : undefined),
    [contacts, task],
  );

  const [channels, setChannels] = useState<Channel[]>(['email']);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [stage, setStage] = useState<Stage>('compose');
  const [previewTab, setPreviewTab] = useState<Channel>('email');
  const [previewEmail, setPreviewEmail] = useState<{ subject: string; html: string } | null>(null);
  const [previewText, setPreviewText] = useState<{ body: string } | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const config = useMemo<EventConfig | null>(() => {
    if (!task) return null;
    if (!isSupportedCommunicationTaskEvent(task.event)) return null;
    return EVENT_CONFIG[task.event];
  }, [task]);

  const accentColor = useMemo(() => {
    if (!config) return theme.palette.primary.main;
    switch (config.accent) {
      case 'secondary':
        return theme.palette.secondary.main;
      case 'success':
        return theme.palette.success.main;
      case 'error':
        return theme.palette.error.main;
      case 'primary':
      default:
        return theme.palette.primary.main;
    }
  }, [config, theme]);

  const senderName = useMemo(() => {
    if (!userInfo) return null;
    const full = `${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim();
    return full || userInfo.email || null;
  }, [userInfo]);

  useEffect(() => {
    if (!open || !task) return;
    setRecipientName(contact?.name || '');
    setRecipientEmail(contact?.email || '');
    setRecipientPhone(contact?.phone || '');
    setPersonalMessage('');
    setPreviewEmail(null);
    setPreviewText(null);
    setStage('compose');
    setError(null);

    // Default channel: honor the user's preference saved on the task. If none,
    // fall back to whichever contact info is available on the linked contact.
    let defaults: Channel[] = task.preferredChannels && task.preferredChannels.length > 0
      ? [...task.preferredChannels]
      : [];
    if (defaults.length === 0) {
      if (contact?.email) defaults.push('email');
      if (contact?.phone) defaults.push('text');
    }
    if (defaults.length === 0) defaults = ['email'];
    setChannels(defaults);
    setPreviewTab(defaults[0]);
  }, [open, task, contact]);

  if (!task || !config) return null;

  const wantsEmail = channels.includes('email');
  const wantsText = channels.includes('text');

  const handleChannelChange = (_: unknown, value: Channel[]) => {
    if (value.length === 0) return;
    setChannels(value);
    if (!value.includes(previewTab)) {
      setPreviewTab(value[0]);
    }
  };

  const validateBeforePreview = (): string | null => {
    if (!recipientName.trim()) return "Add the customer's name before previewing.";
    if (wantsEmail && !recipientEmail.trim()) {
      return "Add the customer's email — or remove the email channel.";
    }
    if (wantsText && !recipientPhone.trim()) {
      return "Add the customer's phone — or remove the text channel.";
    }
    return null;
  };

  const handlePreview = async () => {
    setError(null);
    const validationError = validateBeforePreview();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      const renderParams = {
        customerName: recipientName.trim(),
        personalMessage: personalMessage.trim(),
        senderName: senderName || undefined,
      };
      const [emailResult, textResult] = await Promise.all([
        wantsEmail ? config.renderEmail(renderParams) : Promise.resolve(null),
        wantsText ? config.renderText(renderParams) : Promise.resolve(null),
      ]);
      setPreviewEmail(emailResult);
      setPreviewText(textResult);
      setPreviewTab(wantsEmail ? 'email' : 'text');
      setStage('preview');
    } catch (err) {
      console.error('Failed to render communication preview', err);
      setError('Could not generate the preview. Please try again.');
    }
  };

  const handleSend = async () => {
    setIsSending(true);
    setError(null);
    try {
      const result: SentCommunicationResult = {};
      const sentAt = new Date();

      if (wantsEmail) {
        const ok = await sendEmail({
          to: recipientEmail.trim(),
          customerName: recipientName.trim(),
          personalMessage: personalMessage.trim(),
          senderName,
          templateName: config.emailTemplate,
        });
        if (!ok) {
          setError('We could not queue the email. Please try again.');
          return;
        }
        result.email = {
          recipientEmail: recipientEmail.trim(),
          subject: previewEmail?.subject || '',
          html: previewEmail?.html || '',
          templateName: config.emailTemplate,
          personalMessage: personalMessage.trim(),
          senderName,
          sentAt,
        };
      }

      if (wantsText) {
        const smsResult = await sendText({
          to: recipientPhone.trim(),
          customerName: recipientName.trim(),
          personalMessage: personalMessage.trim(),
          senderName,
          templateName: config.textTemplate,
        });
        if (!smsResult.success) {
          setError('We could not queue the text message. Please try again.');
          return;
        }
        result.text = {
          recipientPhone: smsResult.normalizedPhone || recipientPhone.trim(),
          body: smsResult.body || previewText?.body || '',
          templateName: config.textTemplate,
          personalMessage: personalMessage.trim(),
          senderName,
          sentAt,
          messageDocId: smsResult.messageDocId ?? null,
        };
      }

      await onSent(task, result);
      onClose();
    } catch (err) {
      console.error('Failed to send task communication', err);
      setError('Something went wrong while sending. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const sendButtonLabel = (() => {
    if (wantsEmail && wantsText) return 'Send both & complete task';
    if (wantsText) return 'Send text & complete task';
    return 'Send email & complete task';
  })();

  return (
    <Dialog open={open} onClose={isSending ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(accentColor, 0.15),
              color: accentColor,
            }}
          >
            {config.icon}
          </Box>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {stage === 'compose' ? config.title : 'Preview before sending'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {config.subtitlePrefix}: {task.title}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={stage === 'compose' ? 'Step 1 · Compose' : 'Step 2 · Preview'}
            sx={{
              fontWeight: 700,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.dark,
            }}
          />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {stage === 'compose' ? (
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Send via
              </Typography>
              <ToggleButtonGroup
                value={channels}
                onChange={handleChannelChange}
                size="small"
                color="primary"
              >
                <ToggleButton value="email">
                  <Email fontSize="small" sx={{ mr: 0.75 }} />
                  Email
                </ToggleButton>
                <ToggleButton value="text">
                  <Sms fontSize="small" sx={{ mr: 0.75 }} />
                  Text
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Customer name"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                fullWidth
                required
              />
              {wantsEmail && (
                <TextField
                  label="Customer email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  fullWidth
                  required
                  type="email"
                  InputProps={{
                    startAdornment: (
                      <Email fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                    ),
                  }}
                />
              )}
              {wantsText && (
                <TextField
                  label="Customer phone"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  fullWidth
                  required
                  type="tel"
                  InputProps={{
                    startAdornment: (
                      <Phone fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                    ),
                  }}
                />
              )}
            </Stack>

            <TextField
              label={config.messageLabel}
              value={personalMessage}
              onChange={(e) => setPersonalMessage(e.target.value)}
              multiline
              minRows={5}
              placeholder={config.messagePlaceholder}
              helperText={config.messageHelper}
            />

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(accentColor, 0.06),
                borderLeft: `4px solid ${accentColor}`,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {config.composeIntro} The same personal message is woven into both the email and the
                text where applicable. Click <strong>Preview</strong> to see exactly what
                {' '}
                {recipientName || 'the customer'} will receive before sending.
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Tooltip title="Back to compose">
                <IconButton size="small" onClick={() => setStage('compose')} disabled={isSending}>
                  <ArrowBack fontSize="small" />
                </IconButton>
              </Tooltip>
              {wantsEmail && wantsText && (
                <Tabs
                  value={previewTab}
                  onChange={(_, value: Channel) => setPreviewTab(value)}
                  sx={{ minHeight: 36 }}
                >
                  <Tab
                    icon={<Email fontSize="small" />}
                    iconPosition="start"
                    value="email"
                    label="Email"
                    sx={{ minHeight: 36 }}
                  />
                  <Tab
                    icon={<Sms fontSize="small" />}
                    iconPosition="start"
                    value="text"
                    label="Text"
                    sx={{ minHeight: 36 }}
                  />
                </Tabs>
              )}
            </Stack>

            {previewTab === 'email' && previewEmail && (
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    label={`To: ${recipientEmail}`}
                    sx={{
                      fontWeight: 600,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.dark,
                    }}
                  />
                  <Chip
                    size="small"
                    label={`Subject: ${previewEmail.subject}`}
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.background.default, 0.6),
                    overflow: 'hidden',
                    minHeight: 360,
                  }}
                >
                  <Box
                    component="iframe"
                    title="Email preview"
                    srcDoc={previewEmail.html}
                    sx={{
                      width: '100%',
                      height: { xs: 420, md: 560 },
                      border: 'none',
                      display: 'block',
                      bgcolor: '#f5f5f5',
                    }}
                  />
                </Box>
              </Stack>
            )}

            {previewTab === 'text' && previewText && (
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    icon={<Phone fontSize="inherit" />}
                    label={`To: ${recipientPhone}`}
                    sx={{
                      fontWeight: 600,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.dark,
                    }}
                  />
                  <Chip
                    size="small"
                    label={`${previewText.body.length} characters`}
                    variant="outlined"
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    maxWidth: 420,
                    bgcolor: alpha(theme.palette.success.main, 0.06),
                    borderColor: alpha(theme.palette.success.main, 0.25),
                    whiteSpace: 'pre-wrap',
                    fontFamily:
                      '"SF Pro Text", "Helvetica Neue", "Roboto", "Arial", sans-serif',
                    lineHeight: 1.5,
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {previewText.body}
                  </Typography>
                </Paper>
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isSending}>
          Cancel
        </Button>
        {stage === 'compose' ? (
          <Button
            onClick={handlePreview}
            variant="contained"
            startIcon={<Visibility />}
            disabled={isSending}
          >
            Preview
          </Button>
        ) : (
          <>
            <Button
              onClick={() => setStage('compose')}
              disabled={isSending}
              startIcon={<ArrowBack />}
            >
              Back
            </Button>
            <Button
              onClick={handleSend}
              variant="contained"
              color="primary"
              startIcon={<Send />}
              disabled={isSending}
            >
              {isSending ? 'Sending…' : sendButtonLabel}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CompleteTaskCommunicationDialog;
